import { useState, useEffect } from 'react';
import { Hero } from '../components/sections/Hero';
import { Services } from '../components/sections/ServiceCategorySection';
import { Video } from '../components/sections/Video';
import { Testimonials } from '../components/sections/Testimonials';
import { Partners } from '../components/sections/Partners';
import { TopSellers } from '../components/sections/TopSellers';
import { Workshops } from '../components/sections/Workshops';
import { Faq } from '../components/sections/Faq';
import Layout from '@/components/layout/Index';
import StatsSection from '@/components/sections/StatsSection';
import Loader from '@/components/ui/Loader';
import { client } from '@/sanity/client';
import { useQuery } from '@tanstack/react-query';
import {
	useAboutPage,
	useBlogPosts,
	useTestimonials,
	usePartners,
	useStats,
	useFaq,
	useServicesPage,
	useWorkshops,
	useSiteSettings,
} from '@/hooks/useSanityData';
import NoticeDialog from '@/components/ui/NoticeDialog';

const Index = () => {
	// Track loading states for all data needed on the homepage
	const { isLoading: isLoadingAbout } = useAboutPage();
	const { isLoading: isLoadingBlog } = useBlogPosts();
	const { isLoading: isLoadingTestimonials } = useTestimonials();
	const { isLoading: isLoadingPartners } = usePartners();
	const { isLoading: isLoadingStats } = useStats();
	const { isLoading: isLoadingFaq } = useFaq();
	const { isLoading: isLoadingServices } = useServicesPage();
	const { isLoading: isLoadingWorkshops } = useWorkshops();
	const { data: siteSettings } = useSiteSettings();

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	// Use React Query for hero content to leverage SSR prefetching
	const { data: heroContent, isLoading: isHeroLoading } = useQuery({
		queryKey: ['homeHero'],
		queryFn: async () => {
			return client.fetch(`*[_type == "homePage"][0].hero{
				heading,
				subheading,
				primaryButtonText,
				primaryButtonUrl,
				backgroundImage{
					asset->{
						url
					}
				}
			}`);
		},
		staleTime: 1000 * 60 * 5, // 5 minutes
	});

	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [isContentReady, setIsContentReady] = useState(false);

	// Preload the hero background image
	useEffect(() => {
		if (heroContent?.backgroundImage?.asset?.url && !isImageLoaded) {
			const img = new Image();
			img.src = heroContent.backgroundImage.asset.url;
			img.onload = () => setIsImageLoaded(true);
			img.onerror = () => setIsImageLoaded(true); // Proceed even if image fails to load
		} else if (!heroContent?.backgroundImage?.asset?.url) {
			// If there's no image, consider it loaded
			setIsImageLoaded(true);
		}
	}, [heroContent, isImageLoaded]);

	// Check if any data is still loading
	const isApiLoading =
		isLoadingAbout ||
		isLoadingBlog ||
		isLoadingTestimonials ||
		isLoadingPartners ||
		isLoadingStats ||
		isLoadingFaq ||
		isLoadingServices ||
		isLoadingWorkshops;

	useEffect(() => {
		// Ensure all data is loaded before showing content
		if (!isApiLoading && !isHeroLoading && isImageLoaded) {
			// Add a small delay to ensure smooth loading transition
			const timer = setTimeout(() => {
				setIsContentReady(true);
				if (siteSettings?.bookingNoticeActive) {
					const hasSeenNotice = sessionStorage.getItem('hasSeenNotice');
					if (!hasSeenNotice) {
						setIsDialogOpen(true);
						sessionStorage.setItem('hasSeenNotice', 'true');
					}
				}
			}, 300);

			return () => clearTimeout(timer);
		}
	}, [isApiLoading, isHeroLoading, isImageLoaded, siteSettings]);

	if (isApiLoading || isHeroLoading || !isContentReady || !isImageLoaded) {
		return <Loader />;
	}

	return (
		<div className='flex flex-col w-full overflow-hidden'>
			<Layout>
				<main className='w-full'>
					{siteSettings?.bookingNoticeActive && (
						<NoticeDialog
							isOpen={isDialogOpen}
							onClose={() => setIsDialogOpen(false)}
							noticeTitle={siteSettings.bookingNoticeTitle}
							noticeText={siteSettings.bookingNoticeText}
						/>
					)}
					<div className='relative'>
						<Hero heroContent={heroContent} />
						<div className='relative -mt-12 bg-white rounded-t-[56px] '>
							<Services />
						</div>
					</div>
					<Video />
					<TopSellers />
					<Workshops />
					<Testimonials />
					<Partners />
					<StatsSection />

					<Faq />
				</main>
			</Layout>
		</div>
	);
};

export default Index;
